/**
 * FallbackResultBuilder - Utility for creating fallback PageWindowResult objects
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for creating fallback/empty PageWindowResult objects
 * when pagination fails or cannot be performed (e.g., missing font, WASM errors).
 */

import type { PageWindowResult, ParagraphSnapshot } from '../../../core/types/Typesetter';

/**
 * Create an empty telemetry object with zero values
 * 
 * @returns Empty telemetry object with all metrics set to 0
 */
function createEmptyTelemetry() {
  return {
    pages_emitted: 0,
    lines_scanned: 0,
    paragraphs_processed: 0,
    spill_resumes: 0,
    mem_bytes_peak: 0
  };
}

/**
 * Create a fallback PageWindowResult with empty pages
 * 
 * Used when pagination cannot be performed (e.g., missing font, WASM errors).
 * Returns an empty result that allows the UI to continue rendering without errors.
 * 
 * @param snapshot - Optional paragraph snapshot to include (falls back to empty snapshot if not provided)
 * @param totalPages - Optional total pages count (defaults to 0)
 * @returns Fallback PageWindowResult with empty pages array
 */
export function createFallbackPageWindowResult(
  snapshot?: ParagraphSnapshot | null,
  totalPages: number = 0
): PageWindowResult {
  // Create empty snapshot if none provided
  const fallbackSnapshot: ParagraphSnapshot = snapshot || {
    lines: [],
    caret_map: [],
    total_width_fp: 0,
    total_height_fp: 0,
    plan_hash: '',
    source: 'provisional' as const,
    snapshot_id: 'fallback-empty',
    paragraph_id: 'fallback-para-0',
    engine_versions: {
      core_version: '1.0.0',
      hb_version: '12.0.0',
      rules_version: 'Unicode-15.0',
      font_set_version: 'production-v1'
    }
  };
  
  return {
    pages: [],
    snapshot: fallbackSnapshot,
    next_cursor: null,
    total_pages: totalPages,
    telemetry: createEmptyTelemetry()
  };
}

/**
 * Create a fallback PageWindowResult with cached pages
 * 
 * Used when all requested pages are already cached and no WASM call is needed.
 * 
 * @param cachedPages - Array of cached PageLayout objects
 * @param snapshot - Paragraph snapshot to include
 * @returns PageWindowResult with cached pages
 */
export function createCachedPageWindowResult(
  cachedPages: any[],
  snapshot: ParagraphSnapshot
): PageWindowResult {
  return {
    pages: cachedPages,
    snapshot,
    next_cursor: null,
    total_pages: cachedPages.length,
    telemetry: {
      pages_emitted: cachedPages.length,
      lines_scanned: 0,
      paragraphs_processed: 0,
      spill_resumes: 0,
      mem_bytes_peak: 0
    }
  };
}

